import { useDispatch } from "react-redux";
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { User, getAuth, Unsubscribe, onAuthStateChanged } from "firebase/auth";
import firebase from "app/Auth";
import { AppDispatch } from "app/store";
import { actions as configActions } from "app/configSlice";
import { isDev } from "common/utils";
import { actions } from "features/user/slice";
import { getAccountUsers } from "features/account/api";
import { fetchAccountPlan } from "features/subscription";
import { getParentChatRooms } from "features/message/api";
import { connectEmulator, getParentConfig, getNotifications } from "./api";

export const AuthProvider = (props: { children: React.ReactNode | null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const auth = getAuth(firebase);
  const [isAuthenticating, setIsAuthenticating] = useState(true);

  useEffect(() => {
    if (isDev) {
      connectEmulator(auth);
    }
  }, []);

  useEffect(() => {
    const unsubscribe: Unsubscribe = onAuthStateChanged(
      auth,
      async (user: User | null) => {
        if (user) {
          try {
            dispatch(configActions.setLoading(true));

            // 1. アカウントユーザー取得（必須・先行）
            await dispatch(getAccountUsers({ uid: user.uid })).unwrap();

            // 2. 並列でその他のデータを取得（kidsと同様）
            await Promise.all([
              dispatch(getParentConfig({ uid: user.uid })),
              dispatch(getNotifications()),
              dispatch(fetchAccountPlan({ uid: user.uid })),
              dispatch(getParentChatRooms({ uid: user.uid })),
            ]);

            dispatch(configActions.setLoading(false));
          } catch (error) {
            dispatch(configActions.setLoading(false));
            console.error(error);
          }
        } else {
          // 未ログインの場合はユーザー情報をクリアしてログイン画面へリダイレクト
          dispatch(actions.clearUser());
          // 認証画面以外にいる場合のみリダイレクト
          if (!location.pathname.startsWith("/auth")) {
            navigate("/auth/login");
          }
        }
        setIsAuthenticating(false);
      },
      (error) => console.error(JSON.stringify(error)),
    );

    return () => {
      unsubscribe();
    };
  }, []);

  if (isAuthenticating) {
    return null;
  }

  return <>{props.children}</>;
};
